import React from 'react'
import { CheckCircle } from '@phosphor-icons/react'

import { formatTimeRange } from '@/utils/date/format-time-range'
import { unwrapEmbeddedErrorMessage } from '@/utils/error/unwrap-embedded-error-message'

import DropdownDots from '../../DropdownDots/DropdownDots'
import { formatDateTime, formatPermissions } from '../utils'
import StatusPill from '../components/StatusPill'
import { APP_COLORS } from '../../../helpers/constants'

// Note: flip to true to bring the manual 'Delete Igloo key' row action back — see the note on the
// action itself for why it is off.
const SHOW_DELETE_IGLOO_KEY = false

// Note: Guests/Vendors write actions go through the legacy axios client (src/api/api.js), which
// has no response interceptor -- unlike authAPI, whose interceptors.ts lifts the server message
// onto err.message. So the backend's real reason only ever lives at response.data.message here.
export const getWriteErrorMessage = (err, fallback) => {
  const message = err?.response?.data?.message
  // Some providers' raw JSON error bodies are passed through as the message; unwrap them.
  return typeof message === 'string' && message ? unwrapEmbeddedErrorMessage(message) : fallback
}

const formatAdditionalInfo = (t, typeOrUnit, phone) => {
  if (!typeOrUnit && !phone) {
    return <span>{t('visitors:columns.notAvailable')}</span>
  }

  return (
    <React.Fragment>
      {typeOrUnit && <span>{typeOrUnit}</span>}
      {typeOrUnit && phone && <span> | </span>}
      {phone && <span>{phone}</span>}
    </React.Fragment>
  )
}

export const getGuestColumns = (
  t,
  onCheckInClick,
  onDetailsClick,
  onBanToggle,
  onSuspendToggle,
  onEditGuest,
  timezone,
  onDeleteGuestKey,
  iglooWorkAvailable,
  iglooWorkVisitorAccessAvailable,
) => [
  {
    Header: t('visitors:columns.guestName'),
    accessor: 'guestName',
    Cell: ({ row }) => {
      const name = row.original.name
      const type = row.original.type
      const phone = row.original.phone

      return (
        <div className="guest-name-cell">
          <div>{name}</div>
          <div className="additional-info">{formatAdditionalInfo(t, type, phone)}</div>
        </div>
      )
    },
  },
  {
    Header: t('visitors:columns.permissions'),
    accessor: 'permissions',
    Cell: ({ row }) => {
      const permission = formatPermissions(row.original, timezone)
      const permissionTime = formatTimeRange(row.original.startTime, row.original.endTime, timezone)
      return (
        <div className="permission-cell">
          <div>{permission}</div>
          <div className="permission-time">{permissionTime}</div>
        </div>
      )
    },
  },
  {
    Header: t('visitors:columns.lastCheckIn'),
    accessor: 'lastCheckIn',
    Cell: ({ row }) => {
      if (!row.original.lastCheckIn) {
        return <div>-</div>
      }

      const { date, time } = formatDateTime(row.original.lastCheckIn, timezone)
      return (
        <div className="last-check-in-cell">
          <div>{date}</div>
          <div className="check-in-time">{time}</div>
        </div>
      )
    },
  },
  {
    Header: t('visitors:columns.residentUnit'),
    accessor: 'residentInfo',
    Cell: ({ row }) => {
      const residentName = (row.original.clientId && row.original.residentName) || ''
      const unit = (row.original.clientId && row.original.unit) || ''
      const residentPhone = (row.original.clientId && row.original.residentPhone) || ''

      // If resident name and unit are missing, show a single dash
      if (!residentName && !unit) {
        return <div>-</div>
      }

      return (
        <div className="resident-cell">
          <div>{residentName || '-'}</div>
          <div className="additional-info">{formatAdditionalInfo(t, unit, residentPhone)}</div>
        </div>
      )
    },
  },
  {
    Header: t('visitors:columns.permissionToEnter'),
    Cell: ({ row }) => (
      <div className="permission-to-enter">
        {row.original.permissionToEnter ? <CheckCircle size={18} /> : null}
      </div>
    ),
  },
  {
    Header: t('visitors:columns.status'),
    accessor: 'status',
    Cell: ({ row }) => <StatusPill status={row.original.status} />,
  },
  {
    Header: t('visitors:columns.actions'),
    accessor: 'actions',
    Cell: ({ row }) => (
      <div className="actions" onClick={(e) => e.stopPropagation()}>
        {/* Disable check-in if guest is banned or expired */}
        <button
          className="button button-transparent"
          onClick={() => onCheckInClick(row.original)}
          disabled={row.original.status !== 'active'}
        >
          {t('visitors:columns.checkIn')}
        </button>
        <DropdownDots
          options={[
            {
              id: 1,
              text: t('visitors:columns.details'),
              action: () => onDetailsClick(row.original),
            },
            // Note: 'Resend invitation' (id 6) is hidden for now. The backend endpoint
            // (POST /client/:clientId/guest/:guestId/invite/resend) only re-sends the email for
            // guests that already have keyless or PIN access and 400s for everyone else, and the
            // guest payload carries no signal we could gate on (keyAccess is provider-agnostic and
            // says nothing about PIN-only providers). Restore it once the endpoint is widened, or
            // once the backend returns a canResendInvite flag. The API hook stays in
            // src/api/keyless/igloo/resend-guest-invite.ts.
            {
              id: 2,
              text: row.original.banned ? t('visitors:columns.unban') : t('visitors:columns.ban'),
              styleContainer: { background: APP_COLORS.red100 },
              styleText: { color: APP_COLORS.red500 },
              action: () => onBanToggle(row.original),
            },
            {
              id: 3,
              text: row.original.suspended
                ? t('visitors:columns.unsuspend')
                : t('visitors:columns.suspend'),
              action: () => onSuspendToggle(row.original),
            },
            // Note: banned/suspended guests can't be edited, but the option stays visible and
            // inert (DropdownDots renders a falsy `action` as .is-disabled and swallows the
            // click) so the PM sees why rather than wondering where Edit went.
            {
              id: 4,
              text: t('visitors:columns.edit'),
              action:
                row.original.banned || row.original.suspended
                  ? undefined
                  : () => onEditGuest(row.original),
            },
            // Note: 'Delete Igloo key' is hidden for now (SHOW_DELETE_IGLOO_KEY). Revoking a
            // visitor key was never requested as a manual action — EL-11154 revokes keys as a
            // side effect of ban/suspend and of the resident being deactivated or deleted, which
            // is what staff should use. The wiring is left in place so the action can be brought
            // back if product asks for it.
            // Note: keyAccess is provider-agnostic (it's OR-collapsed with Salto Space's own
            // toggle on create — see Visitors/modals/utils.jsx), so it can't tell an Igloo key
            // from a Salto key on its own. Gating on iglooWorkVisitorAccessAvailable too keeps
            // this action off buildings where a guest couldn't have gotten an Igloo key at all.
            // A building with BOTH IglooWorks and Salto Space enabled is still ambiguous — there
            // is no per-provider signal from the backend yet (EL-12844).
            ...(SHOW_DELETE_IGLOO_KEY &&
            iglooWorkAvailable &&
            iglooWorkVisitorAccessAvailable &&
            row.original.keyAccess &&
            !row.original.banned &&
            !row.original.suspended
              ? [
                  {
                    id: 5,
                    text: t('visitors:columns.deleteIglooKey'),
                    styleContainer: { background: APP_COLORS.red100 },
                    styleText: { color: APP_COLORS.red500 },
                    action: () => onDeleteGuestKey(row.original),
                  },
                ]
              : []),
          ]}
        />
      </div>
    ),
  },
]
